import clsx from "clsx";
import LystLogo from "img/branding/lyst-logo-2020-black.svg?inline";
import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector as useReduxSelector } from "react-redux";
import { JsonLd } from "react-schemaorg";
import { WebSite } from "schema-dts";
import { KevelCampaignControl } from "web/react/components/kevel-campaign-control/kevel-campaign-control";
import HeaderGenders from "web/react/components/navigation/header-genders/header-genders";
import { MainNavigationToggleMobile } from "web/react/components/navigation/navigation-menu/main-navigation-toggle-mobile/main-navigation-toggle-mobile";
import { useNavigationContext } from "web/react/components/navigation/navigation.context";
import { UserDropdownProps } from "web/react/components/navigation/pre-header-bar/user-dropdown";
import { SearchBar } from "web/react/components/navigation/search-bar";
import { AutoSuggestProvider } from "web/react/components/navigation/search-bar/auto-suggest/auto-suggest.context";
import AccountLinks from "web/react/components/navigation/user-links";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { HStack } from "web/react/emo/h-stack";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { addLystMatte, removeLystMatte } from "web/redux/ducks/layout";
import { ReduxStoreState } from "web/redux/store";
import globals from "web/script/modules/globals";
import { languageIsEnglish } from "web/script/modules/language";
import { AppHeaderBarSerializer } from "web/types/serializers";
import styles from "./header-bar.module.css";
import { SearchButton } from "./search-button";

export interface HeaderBarProps extends AppHeaderBarSerializer {
    userDropdown: UserDropdownProps;
    registerUrl: string;
}

// This is a workaround for the fact that the JsonLd potentialAction property
// does not include the "query-input" property
type CustomWebSite = WebSite & {
    potentialAction: {
        "@type": "SearchAction";
        target: string;
        "query-input": string;
    };
};

function extractState(state: ReduxStoreState): boolean {
    const { isSearchOpen, isCalloutOpen } = state.appHeaderBarReducer;
    return isSearchOpen || isCalloutOpen;
}

export function HeaderBar({
    home_url: homeUrl,
    is_homepage: isHomePage,
    genders,
    is_authenticated: isAuthenticated,
    my_searches_url: mySearchesUrl,
    saved_item_url: savedItemUrl,
    notifications_url: notificationsUrl,
    notifications_count: notificationsCount,
    search_input: searchInput,
    absolute_home_url: absoluteHomeUrl,
    absolute_action_url: absoluteActionUrl,
    has_items_in_wishlist: hasItemsInWishlist,
    registerUrl,
}: HeaderBarProps): React.ReactElement {
    const showMatte = useReduxSelector(extractState);

    const { showMenu, isSearchOpen, handleHeaderAnalytics } = useNavigationContext();
    const { isDesktopViewport, isMobileViewport, isTabletViewport } = useDomViewport();
    const mobileView = isMobileViewport || isTabletViewport;
    const dispatch = useDispatch();
    useEffect(() => {
        if (showMatte) {
            dispatch(addLystMatte());
        } else {
            dispatch(removeLystMatte());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showMatte]);
    function navigateToHomePage(): void {
        globals.window.location.href = homeUrl;
    }

    return (
        <div className={styles.headerBar}>
            <div className={styles.headerBarContainer}>
                <div className={styles.headerBarInner}>
                    <a className={clsx(styles.logo, "hidden-tablet")} href={homeUrl}>
                        <LystLogo />
                    </a>

                    <div className={styles.headerBarLeftContainer}>
                        <div className="hidden-desktop">
                            <MainNavigationToggleMobile />
                        </div>

                        <HeaderGenders genders={genders} />

                        <AutoSuggestProvider>
                            {isDesktopViewport && (
                                <SearchBar searchInput={searchInput} isMobile={false} />
                            )}
                            <div className="hidden-desktop">
                                <SearchButton />
                            </div>
                            {/** Use portal to render into div if not desktop @see navigation.tsx */}
                            {isSearchOpen &&
                                ReactDOM.createPortal(
                                    <SearchBar searchInput={searchInput} isMobile={true} />,
                                    document.getElementById(
                                        "app-header-search-bar-mobile"
                                    ) as HTMLDivElement
                                )}
                            {isHomePage && (
                                <JsonLd<CustomWebSite>
                                    item={{
                                        "@context": "https://schema.org",
                                        "@type": "WebSite",
                                        url: absoluteHomeUrl,
                                        potentialAction: {
                                            "@type": "SearchAction",
                                            target: `${absoluteActionUrl}?q={q}`,
                                            "query-input": "name=q",
                                        },
                                    }}
                                />
                            )}
                        </AutoSuggestProvider>
                    </div>

                    <KevelCampaignControl
                        captureType="header_logo"
                        contextType={"header_logo"}
                        analyticsEventLabel={"header_logo"}
                        defaultAction={navigateToHomePage}
                        continueOptionHref={homeUrl}
                    >
                        <a className={clsx(styles.logo, "hidden-desktop")} href={homeUrl}>
                            <LystLogo />
                        </a>
                    </KevelCampaignControl>

                    {languageIsEnglish() ? (
                        <div className={styles.glyphsContainer}>
                            {isAuthenticated && (
                                <AccountLinks
                                    my_searches_url={mySearchesUrl}
                                    saved_item_url={savedItemUrl}
                                    notifications_url={notificationsUrl}
                                    notifications_count={notificationsCount}
                                    has_items_in_wishlist={hasItemsInWishlist}
                                />
                            )}
                            <HStack spacing={isDesktopViewport ? "xl" : "xs"} align="center">
                                {mobileView && (
                                    <>
                                        <a
                                            className={styles.iconContainer}
                                            href={
                                                isAuthenticated && notificationsUrl
                                                    ? notificationsUrl
                                                    : registerUrl
                                            }
                                            onClick={() => {
                                                handleHeaderAnalytics("top_nav", "alerts_icon");
                                            }}
                                        >
                                            <SVGIcon
                                                name={"bell-nav"}
                                                className={clsx(styles.icon, styles.bellIcon, {
                                                    [styles.iconGrey]: showMenu || isSearchOpen,
                                                })}
                                            />
                                        </a>

                                        <a
                                            className={styles.iconContainer}
                                            href={
                                                isAuthenticated && savedItemUrl
                                                    ? savedItemUrl
                                                    : registerUrl
                                            }
                                            onClick={() => {
                                                handleHeaderAnalytics("top_nav", "wishlist_icon");
                                            }}
                                        >
                                            <SVGIcon
                                                name={"heart-icon-nav"}
                                                className={clsx(styles.icon, styles.heartIcon, {
                                                    [styles.iconGrey]: showMenu || isSearchOpen,
                                                })}
                                            />
                                        </a>
                                    </>
                                )}
                            </HStack>
                        </div>
                    ) : (
                        <div
                            className={clsx(styles.glyphsContainer, "hidden-desktop")}
                        /> /* maintain flex layout for non-english mobile header bar */
                    )}
                </div>
            </div>
        </div>
    );
}
